import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { agentApiFixed as agentApi } from "@/lib/api/agent-api-fixed";
import { AgentLog } from "@/types/agent-types";
import { format } from "date-fns";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ReplayLog } from "./ReplayLog";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronDown,
  ChevronUp,
  Search,
  RefreshCw,
  Play,
  Code,
  FileJson,
  List,
  ListFilter,
  X,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";

interface AgentLogViewerProps {
  log?: AgentLog;
  showRatingControls?: boolean;
}

export const AgentLogViewer: React.FC<AgentLogViewerProps> = ({ log, showRatingControls }) => {
  // Function to pretty print JSON
  const prettyPrintJSON = (json: any) => {
    try {
      if (typeof json === 'string') {
        json = JSON.parse(json);
      }
      return JSON.stringify(json, null, 2);
    } catch (e) {
      return json;
    }
  };

  // If a specific log is provided, render just that log
  if (log) {
    return (
      <Card className="w-full mt-4">
        <CardHeader>
          <CardTitle>Agent Execution Result</CardTitle>
          <CardDescription>
            Test execution completed at {format(new Date(log.timestamp), "MMM d, yyyy h:mm:ss a")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center text-sm font-medium">
                  <Code className="h-4 w-4 mr-2 text-muted-foreground" />
                  Prompt
                </div>
                <div className="bg-card border rounded-md p-3 overflow-auto max-h-[300px]">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {log.prompt}
                  </pre>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm font-medium">
                  <FileJson className="h-4 w-4 mr-2 text-muted-foreground" />
                  Output
                </div>
                <div className="bg-card border rounded-md p-3 overflow-auto max-h-[300px]">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {prettyPrintJSON(log.parsed_output)}
                  </pre>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/30 p-4 rounded-md">
              <h4 className="text-sm font-medium mb-2">Execution Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Agent Type:</div>
                <div className="font-medium">{log.agent_type}</div>
                
                <div>Trace ID:</div>
                <div className="font-mono text-xs">{log.traceId}</div>
                
                <div>Total Duration:</div>
                <div className="font-medium">{log.duration}ms</div>
                
                {log.metadata?.tokens_used && (
                  <>
                    <div>Tokens Used:</div>
                    <div className="font-medium">{log.metadata.tokens_used}</div>
                  </>
                )}
                
                {log.metadata?.kafka_enabled && (
                  <>
                    <div>Processing:</div>
                    <div className="font-medium">Kafka-enabled real agent</div>
                  </>
                )}
              </div>
            </div>
            
            {showRatingControls && (
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast({
                    title: "Rating submitted",
                    description: "Thank you for rating this execution!",
                  })}
                >
                  <ThumbsUp className="h-4 w-4 mr-1" />
                  Good Result
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast({
                    title: "Rating submitted",
                    description: "Thank you for your feedback!",
                  })}
                >
                  <ThumbsDown className="h-4 w-4 mr-1" />
                  Poor Result
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Otherwise, show the regular multi-log viewer with filters
  const [selectedAgentType, setSelectedAgentType] = useState<string>("");
  const [traceId, setTraceId] = useState<string>("");
  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({});
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isReplayModalOpen, setIsReplayModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AgentLog | null>(null);

  // Fetch available agent types
  const { data: agentTypes = [] } = useQuery({
    queryKey: ["agentTypes"],
    queryFn: () => agentApi.getAgentTypes(),
  });

  // Fetch logs with filters
  const {
    data: logsData,
    isLoading: isLoadingLogs,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ["agentLogs", selectedAgentType, traceId, page, limit],
    queryFn: () => agentApi.getLogs({
      agentType: selectedAgentType || undefined,
      traceId: traceId || undefined,
      limit,
      offset: (page - 1) * limit,
    }),
  });

  const logs = logsData?.logs || [];
  const totalLogs = logsData?.total || 0;
  const totalPages = Math.ceil(totalLogs / limit);

  const toggleLogExpand = (logId: string) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const handleAgentTypeChange = (value: string) => {
    setSelectedAgentType(value);
    setPage(1); // Reset to first page on filter change
  };

  const handleTraceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTraceId(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on search
    refetchLogs();
  };

  const clearFilters = () => {
    setSelectedAgentType("");
    setTraceId("");
    setPage(1);
  };

  const handleReplayClick = (log: AgentLog) => {
    setSelectedLog(log);
    setIsReplayModalOpen(true);
  };

  const formatTimestamp = (timestamp: string | number | Date) => {
    try {
      return format(new Date(timestamp), "MMM d, yyyy h:mm:ss a");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Function to render badges for agent types
  const renderAgentTypeBadge = (agentType?: string) => {
    if (!agentType) {
      return <Badge variant="secondary">Unknown</Badge>;
    }
    const typeColors: Record<string, { bg: string; text: string }> = {
      'page-role': { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-300' },
      'intent': { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-800 dark:text-purple-300' },
      'content': { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-300' },
      'default': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-300' },
    };

    const color = typeColors[agentType] || typeColors.default;
    
    return (
      <Badge className={`${color.bg} ${color.text} border-0`}>{agentType}</Badge>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle>Agent Logs</CardTitle>
            <CardDescription>
              View and analyze agent execution logs
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
              className="flex items-center gap-1"
            >
              <ListFilter className="h-4 w-4" />
              Filters
              {isFiltersPanelOpen ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchLogs()}
              className="flex items-center gap-1"
              disabled={isLoadingLogs}
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingLogs ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <Collapsible open={isFiltersPanelOpen} onOpenChange={setIsFiltersPanelOpen}>
        <CollapsibleContent>
          <div className="px-6 pb-4 border-b">
            <form onSubmit={handleSearchSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="agent-type-filter" className="text-sm font-medium">
                    Agent Type
                  </label>
                  <Select value={selectedAgentType} onValueChange={handleAgentTypeChange}>
                    <SelectTrigger id="agent-type-filter">
                      <SelectValue placeholder="All agent types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All agent types</SelectItem>
                      {agentTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="trace-id" className="text-sm font-medium">
                    Trace ID
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="trace-id"
                      type="text"
                      placeholder="Enter trace ID"
                      value={traceId}
                      onChange={handleTraceIdChange}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFilters}
                  disabled={!selectedAgentType && !traceId}
                  className="text-sm h-8 px-2"
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear filters
                </Button>
                <Button type="submit" size="sm">
                  <Search className="h-3.5 w-3.5 mr-1" />
                  Search Logs
                </Button>
              </div>
            </form>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <CardContent className="p-0">
        {isLoadingLogs ? (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="animate-spin h-6 w-6 text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading logs...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <List className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No logs found</h3>
            <p className="text-muted-foreground">
              {selectedAgentType || traceId
                ? "Try clearing your filters or search terms"
                : "No agent logs are available in the system"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead className="w-[180px]">Trace ID</TableHead>
                    <TableHead className="w-[120px]">Agent Type</TableHead>
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead>Input</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <React.Fragment key={log._id}>
                      <TableRow className="hover:bg-muted/50">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleLogExpand(log._id || log.traceId)}
                            className="h-8 w-8"
                          >
                            {expandedLogs[log._id || log.traceId] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.traceId.substring(0, 8)}...
                        </TableCell>
                        <TableCell>
                          {renderAgentTypeBadge(log.agent_type)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell className="truncate max-w-[300px]">
                          {typeof log.input === 'object'
                            ? JSON.stringify(log.input).substring(0, 50) + '...'
                            : String(log.input).substring(0, 50) + '...'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReplayClick(log)}
                            title="Replay this log"
                            className="h-8 w-8"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>

                      {expandedLogs[log._id || log.traceId] && (
                        <TableRow key={`${log._id}-expanded`}>
                          <TableCell colSpan={6} className="bg-muted/30 p-0">
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                  <div className="flex items-center text-sm font-medium">
                                    <Code className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Prompt
                                  </div>
                                  <div className="bg-card border rounded-md p-3 overflow-auto max-h-[300px]">
                                    <pre className="text-xs font-mono whitespace-pre-wrap">
                                      {log.prompt}
                                    </pre>
                                  </div>
                                </div>

                                <div className="space-y-3">
                                  <div className="flex items-center text-sm font-medium">
                                    <FileJson className="h-4 w-4 mr-2 text-muted-foreground" />
                                    Output
                                  </div>
                                  <div className="bg-card border rounded-md p-3 overflow-auto max-h-[300px]">
                                    <pre className="text-xs font-mono whitespace-pre-wrap">
                                      {prettyPrintJSON(log.parsed_output)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalLogs)} of {totalLogs} logs
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <div className="text-sm">
                    Page {page} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Replay Log Modal */}
      {selectedLog && (
        <ReplayLog
          log={selectedLog}
          open={isReplayModalOpen}
          onOpenChange={setIsReplayModalOpen}
        />
      )}
    </Card>
  );
};